import type { Graph3DAvatarNodeData, Graph3DData } from '@packages/graph-3d';
import type {
  OrgNodeData,
  OrganizationGraphCompileResult,
} from '../utils/organizationGraph';

export type OrganizationGraph3DNodeData = {
  kind: 'company' | 'department' | 'person';
  originalName: string;
  themeColor: string;
  themeSoftBg: string;
} & Graph3DAvatarNodeData;

export interface OrganizationGraph3DEdgeData {
  themeColor: string;
  edgeKind: 'org';
}

const ORG_Z_BY_KIND: Record<OrgNodeData['kind'], number> = {
  company: 0,
  department: 16,
  person: 32,
};

export function buildOrganizationGraph3DData(
  graph: OrganizationGraphCompileResult
): Graph3DData<OrganizationGraph3DNodeData, OrganizationGraph3DEdgeData> {
  const nodes: Graph3DData<
    OrganizationGraph3DNodeData,
    OrganizationGraph3DEdgeData
  >['nodes'] = [];
  const edges: Graph3DData<
    OrganizationGraph3DNodeData,
    OrganizationGraph3DEdgeData
  >['edges'] = [];

  graph.elements.forEach(element => {
    if (element.group === 'nodes') {
      const id = String(element.data.id || '');
      const node = graph.nodeMap.get(id);
      if (!id || !node) {
        return;
      }
      const position = element.position;
      nodes.push({
        id,
        label: node.kind === 'person' ? node.originalName : node.label,
        type: node.kind,
        depth: node.kind === 'company' ? 0 : node.kind === 'department' ? 1 : 2,
        position: {
          x: position?.x ?? 0,
          y: -(position?.y ?? 0),
          z: ORG_Z_BY_KIND[node.kind],
        },
        size: {
          width: node.nodeWidth,
          height: node.nodeHeight,
          depth: node.kind === 'person' ? 14 : 20,
        },
        data: {
          kind: node.kind,
          originalName: node.originalName,
          themeColor: node.kind === 'company' ? '#111111' : node.themeColor,
          themeSoftBg: node.kind === 'company' ? '#e5e7eb' : node.themeSoftBg,
          subtitle: node.subtitle,
          role: node.subtitle,
          avatarUrl: node.avatarUrl,
          avatarFallbackUrls: [],
        },
      });
      return;
    }

    const source = String(element.data.source || '');
    const target = String(element.data.target || '');
    if (!source || !target) {
      return;
    }
    edges.push({
      id: String(element.data.id || `e_${source}_${target}`),
      source,
      target,
      type: 'hierarchy',
      data: {
        themeColor: String(element.data.themeColor || '#64748b'),
        edgeKind: 'org',
      },
    });
  });

  return {
    nodes,
    edges,
  };
}
